<template>
  <div>
    <load-profile />
    <!-- Table Container Card -->
    <b-card>
      <validation-observer
        ref="editForm"
        #default="{ invalid }"
      >
        <!-- form -->
        <n-form-confirmation
          key="editForm"
          :form="$refs.editForm"
          :disabled="invalid"
          @submit="validateForm"
        >
          <div style="width: 100%; display: flex; gap: 1rem;">
            <!-- Department Name with Language Selection -->
            <validation-provider
              v-slot="{ errors }"
              :name="$t('field.departmentName')"
              :rules="
                locale === $i18n.locale.split('-')[0]
                  ? 'required|max:100'
                  : 'max:100'
              "
              style="width: 100%;"
            >
              <b-form-group
                :label="$t('field.departmentName')"
                :state="errors.length === 0 ? null : false"
              >
                <div class="d-flex align-items-center">
                  <div class="flex-grow-1 mr-1">
                    <b-form-input
                      v-model="data.name[locale]"
                      :placeholder="$t('field.departmentName')"
                      :state="errors.length === 0 ? null : false"
                    />
                  </div>
                  <div style="min-width: 80px">
                    <b-form-select
                      v-model="locale"
                      :options="locales"
                      class="language-select"
                    />
                  </div>
                </div>
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
              :name="$t('breadcrumb.workingSchedule')"
              style="width: 100%;"
            >
              <b-form-group
                :label="$t('breadcrumb.workingSchedule')"
                :state="errors.length === 0 ? null : false"
                class="mb-2"
              >
                <n-async-single-select
                  v-model="data.workScheduleId"
                  repository="workingScheduleTemplate"
                  selection-key="id"
                  selection-label="translations"
                  :state="errors.length === 0 ? null : false"
                  :placeholder="$t('breadcrumb.workingSchedule')"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </div>

          <b-row>
            <b-col
              cols="12"
              class="text-right"
            >
              <n-button-loading
                v-if="
                  $can('update', permissionType)
                "
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                type="submit"
                class="mt-1 mr-1"
                variant="primary"
                :loading="loading"
                :disabled="invalid || !data.name[locale]"
              >
                {{ $t("button.saveChanges") }}
              </n-button-loading>
              <n-button-loading
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                type="button"
                variant="outline-secondary"
                class="mt-1"
                @submit="back"
              >
                {{ $t("button.cancel") }}
              </n-button-loading>
            </b-col>
          </b-row>
        </n-form-confirmation>
      </validation-observer>
    </b-card>
  </div>
</template>

<script>
import {
  BForm,
  BFormGroup,
  BFormInput,
  BRow,
  BCol,
  BCard,
  BCardText,
  BLink,
  BFormSelect,
} from 'bootstrap-vue'
import Repository from '@/repositories/RepositoryFactory'
import Ripple from 'vue-ripple-directive'
import NButtonLoading from '@/components/NButtonLoading'
import NFormConfirmation from '@/components/NFormConfirmation'
import NAsyncSingleSelect from '@/components/NAsyncSingleSelect.vue'
import useJwt from '@/auth/jwt/useJwt'

import formInput from './updateFormInput'

const DepartmentRepository = Repository.get('department')
const ProjectRepository = Repository.get('project')

export default {
  components: {
    BForm,
    BFormGroup,
    BFormInput,
    BRow,
    BCol,
    BCard,
    BCardText,
    BLink,
    NButtonLoading,
    NFormConfirmation,
    NAsyncSingleSelect,
    BFormSelect,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      data: {
        name: {
          en: '',
          zh: '',
          km: '',
          vi: '',
        },
        isActive: false,
        workScheduleId: null,
      },
      initData: {},
      permissions: [],
      loading: false,
      file: null,
      locale: this.$i18n.locale.split('-')[0],
      locales: [
        { value: 'en', text: 'English' },
        { value: 'zh', text: '中文' },
        { value: 'km', text: 'ខ្មែរ' },
        { value: 'vi', text: 'Tiếng Anh' },
      ],
    }
  },
  computed: {
    userData() {
      return this.$store.state.profile?.email || this.$store.state.profile?.phone
        ? this.$store.state.profile
        : JSON.parse(
          localStorage.getItem(useJwt.jwtConfig.storageUserDataKeyName),
        )
    },
    dataExist() {
      return !!this.data
    },
  },
  created() {
  },
  beforeDestroy() {
  },
  mounted() {
    this.show()
  },
  methods: {
    getLabel(option) {
      return option.title || ''
    },
    formatErrorMessage(message) {
      return message ? this.$t(message) : ''
    },
    // Helper method to convert string name to object
    convertNameToObject(name) {
      if (typeof name === 'string') {
        const defaultName = name
        const nameObj = {
          en: defaultName,
          zh: '',
          km: '',
          vi: '',
        }
        // Set the current locale's value
        const currentLocale = this.$i18n.locale.split('-')[0]
        if (['en', 'zh', 'km', 'vi'].includes(currentLocale)) {
          nameObj[currentLocale] = defaultName
        }
        return nameObj
      }
      return name
    },
    show() {
      DepartmentRepository.show(this.$route.params.id)
        .then(response => {
          const data = response?.data?.data

          // Handle case where name is a string instead of an object
          const nameObj = this.convertNameToObject(data.name);

          this.initData = {
            ...data,
            name: nameObj,
            projectId: data?.project?.map(project => project.id),
          }

          // Initialize the data object with the name
          this.data.name = { ...nameObj }
          this.data.projectId = data?.project?.map(project => project.id)
          this.data.workScheduleId = data.workScheduleId
          this.data.isActive = data.isActive
        })
        .catch(error => {
          console.error('Error fetching department:', error)
        })
        .then(() => {})
    },
    validateForm() {
      this.$refs.editForm.validate().then(success => {
        if (success) {
          if (this.data.image) {
            this.uploadImage()
          } else {
            this.submit()
          }
        }
      })
    },
    async submit() {
      this.loading = true

      // Ensure name is an object before submission
      const formData = { ...this.data }
      formData.name = this.convertNameToObject(formData.name)

      DepartmentRepository.update(this.$route.params.id, formData)
        .then(() => {
          this.$router.push({
            name: `list-${this.route}`,
          })
        })
        .catch(error => {
          if (error.response?.status === 422) {
            this.$refs.editForm.setErrors(error.response?.data?.message)
          }
        })
        .then(() => {
          this.loading = false
        })
    },
    back() {
      this.$router.back()
    },
  },
  setup() {
    const fields = formInput
    const permissionType = 'manage-users'
    const route = 'department'
    return {
      fields,
      permissionType,
      route,
    }
  },
  async beforeRouteEnter(to, from, next) {
    if (!to.params.projectId) {
      return next()
    }

    try {
      const response = await ProjectRepository.show(to.params.projectId)
      const data = response?.data?.data
      if (!data || !data.id) {
        return next({ name: 'list-project-department' })
      }

      const getProjectName = projectData => {
        const locale = localStorage.getItem('locale') || 'en'
        return projectData.name?.[locale] || projectData.name?.zh || projectData.name?.en || projectData.name?.km || projectData.name?.vi || ''
      }

      const breadcrumb = [
        {
          text: getProjectName(data),
          to: { name: 'list-project' },
        },
        {
          text: 'breadcrumb.list_department',
          to: { name: 'list-project-department' },
        },
        {
          text: 'breadcrumb.update_department',
          active: true,
        },
      ]

      return next(vm => {
        vm.$route.meta.breadcrumb = breadcrumb
      })
    } catch (error) {
      console.error('Failed to fetch department:', error)
      return next({ name: 'list-project' })
    }
  },
  watch: {
    '$i18n.locale': function (val) {
      const [language] = val.split('-')
      this.locale = language
    },
  },
}
</script>

<style lang="scss">
.group-department label.d-block {
  margin-bottom: 0 !important;
  padding-bottom: calc(0.438rem + 1px);
}

.language-select {
  height: 100%;
}
</style>
